import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Text,
  useDisclosure
} from '~/design_system'
import { ActivityLogRow } from '~/components/v2/ActivityLog'
import { Msg } from '~/common/localizationUtils'
import { $thm } from '~/common/themeSx'
import { Timestamp } from '~/components/Timestamp'
import { Log } from '~/settings/settingsApi'
import { Timesheet } from '~/icons'
import { MutedSkeleton } from '~/common/skeletons'

export const DEFAULT_ACTIVITY_LOG_PAGE_SIZE = 30

export const ActivityLogDrawer = ({
  logs = [],
  isOpen = false,
  onClose,
  showLoadMore = false,
  isFetchingNextPage = false,
  onLoadMore
}: {
  logs: Log[]
  isOpen: boolean
  onClose: () => void
  showLoadMore?: boolean
  isFetchingNextPage?: boolean
  onLoadMore?: () => void
}) => {
  const hasLogs = !!logs.length
  return (
    <Drawer size="md" isOpen={isOpen} onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent data-testid="activity-log-drawer">
        <DrawerCloseButton data-testid="activity-log-drawer-close-button" />
        <DrawerHeader
          borderBottom="1px solid"
          borderBottomColor="gray.200"
          sx={$thm({ borderBottom: { $canvas: 'none' } })}>
          <Box>
            <Msg id="term.activity_log" />
          </Box>
          {logs[0] && (
            <Text textStyle="subtextSmall">
              <Msg id="common.ui.term_colon" term="Last Updated" /> <Timestamp datetime={logs[0].date} />
            </Text>
          )}
        </DrawerHeader>
        <DrawerBody data-testid="activity-log-drawer-container" px={0}>
          {hasLogs ? (
            logs.map(log => (
              <Box
                key={log?.log_id}
                sx={$thm({ borderColor: { $base: 'gray.200', $canvas: 'system.color.border.divider' } })}
                borderTopWidth="1px"
                _first={{ borderTop: 'none' }}
                p={6}
                _last={{ pb: 0 }}>
                <ActivityLogRow log={log} />
              </Box>
            ))
          ) : (
            <Text textStyle="body" p={6}>
              <Msg id="activity_log.no_activity" description="No activity has been recorded." />
            </Text>
          )}
          {showLoadMore && (
            <Box display="flex" px={6} justifyContent="center">
              <Button variant="inlineLink" isLoading={isFetchingNextPage} onClick={() => onLoadMore && onLoadMore()}>
                <Msg id="activity_log.load_more" description="Load More" />
              </Button>
            </Box>
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" onClick={onClose}>
            <Msg id="common.ui.close" />
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

type ActivityLogDrawerContainerProps = {
  activityLogs?: Log[]
  isLoading: boolean
  showLoadMore?: boolean
  isFetchingNextPage?: boolean
  onLoadMore?: () => void
  showActivityText?: boolean
}

export const ActivityLogDrawerContainer = ({
  activityLogs = [],
  isLoading,
  showLoadMore = false,
  isFetchingNextPage = false,
  onLoadMore,
  showActivityText = true
}: ActivityLogDrawerContainerProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const legacyStylesOverrides = { '& p': { m: 0 } }
  const hasLogs = !!activityLogs.length
  let latestActivityLogText
  if (!hasLogs) {
    latestActivityLogText = <Msg id="activity_log.no_activity_log_drawer" />
  } else {
    const latestActivityLog = activityLogs[0]
    latestActivityLogText = (
      <Msg
        id="activity_log.last_updated_by"
        lastUpdatedByUser={latestActivityLog?.user_full_name}
        lastUpdatedAtDate={<Timestamp datetime={latestActivityLog?.date} />}
      />
    )
  }

  return (
    <>
      <MutedSkeleton isLoaded={!isLoading} maxW="450px">
        <Box sx={legacyStylesOverrides}>
          <Flex direction={['column', 'row']} gap={2} alignItems="flex-start">
            {showActivityText && (
              <HStack>
                <Timesheet
                  w="16px"
                  sx={$thm({ color: { $base: 'gray.500', $canvas: 'system.color.fg.muted.soft' } })}
                />
                <Text textStyle="bodySmall">{latestActivityLogText}</Text>
              </HStack>
            )}
            <Button
              alignSelf="center"
              variant="inlineLink"
              onClick={onOpen}
              data-testid="view-activity-log-button"
              fontSize="sm"
              sx={{ '@media print': { display: 'none' } }}>
              <Msg
                id="activity_log.button.view_activity_log_drawer"
                term_activity_log={<Msg id="term.activity_log" />}
              />
            </Button>
          </Flex>
        </Box>
      </MutedSkeleton>
      <ActivityLogDrawer
        logs={activityLogs}
        isOpen={isOpen}
        onClose={onClose}
        showLoadMore={showLoadMore}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={onLoadMore}
      />
    </>
  )
}
